import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

const FONT_SANS = "'Geist', 'SF Pro Display', -apple-system, sans-serif";
const FONT_MONO = "'Geist Mono', 'SF Mono', monospace";

export default function ChamaAnalytics({ chamaId, onBack }) {
  const { T } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => { fetchAnalytics(); }, [chamaId]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/chamas/${chamaId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to load analytics');
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const card = {
    background: T.surface, border: `1px solid ${T.border}`,
    borderRadius: 12, padding: '20px 24px',
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${T.border}`, borderTop: `2px solid ${T.accent}`, animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text }}>
      <TopBar T={T} onBack={onBack} />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>📊</div>
        <div style={{ color: T.text, fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Analytics Unavailable</div>
        <div style={{ color: T.textMuted, fontSize: 13, marginBottom: 24 }}>{error}</div>
        <button onClick={onBack} style={{ background: T.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: FONT_SANS }}>
          Go Back
        </button>
      </div>
    </div>
  );

  const { volume_by_day, top_markets, member_breakdown, wallet_flow } = data;

  // Derived stats
  const totalVolume   = volume_by_day.reduce((s, d) => s + parseFloat(d.volume || 0), 0);
  const totalTrades   = volume_by_day.reduce((s, d) => s + parseInt(d.trade_count || 0), 0);
  const totalWins     = volume_by_day.reduce((s, d) => s + parseInt(d.wins || 0), 0);
  const totalLosses   = volume_by_day.reduce((s, d) => s + parseInt(d.losses || 0), 0);
  const winRate       = totalWins + totalLosses > 0 ? ((totalWins / (totalWins + totalLosses)) * 100).toFixed(1) : '—';
  const maxVol        = Math.max(...volume_by_day.map(d => parseFloat(d.volume || 0)), 1);

  const flowMap = {};
  wallet_flow.forEach(f => { flowMap[f.type] = { total: parseFloat(f.total), count: parseInt(f.count) }; });

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

      <TopBar T={T} onBack={onBack} />

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 80px', animation: 'fadeIn 0.4s ease' }}>

        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: '30d Volume',   value: `KES ${totalVolume.toLocaleString()}`,   color: T.yes    },
            { label: 'Total Trades', value: totalTrades,                              color: T.accent },
            { label: 'Win Rate',     value: `${winRate}%`,                            color: T.gold   },
            { label: 'Deposits In',  value: `KES ${(flowMap.deposit?.total || 0).toLocaleString()}`, color: T.text },
          ].map((s, i) => (
            <div key={i} style={{ ...card, padding: '16px 20px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color, fontFamily: FONT_MONO, letterSpacing: '-0.02em' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Volume chart */}
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 20 }}>
            30-Day Trade Volume
          </div>
          {volume_by_day.length === 0 ? (
            <div style={{ color: T.textDim, fontSize: 13, textAlign: 'center', padding: '32px 0' }}>No trades in the last 30 days</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120 }}>
              {volume_by_day.map((d, i) => {
                const h = Math.max((parseFloat(d.volume || 0) / maxVol) * 100, 2);
                const date = new Date(d.day);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div title={`KES ${parseFloat(d.volume || 0).toLocaleString()} · ${d.trade_count} trades`}
                      style={{ width: '100%', height: `${h}%`, background: T.accent, borderRadius: 3, opacity: 0.8, transition: 'opacity 0.15s', cursor: 'default' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0.8}
                    />
                    {i % 5 === 0 && (
                      <div style={{ fontSize: 9, color: T.textDim, fontFamily: FONT_MONO, whiteSpace: 'nowrap' }}>
                        {date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top markets + wallet flow */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

          {/* Top markets */}
          <div style={{ ...card }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Top Markets</div>
            {top_markets.length === 0 ? (
              <div style={{ color: T.textDim, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No trades yet</div>
            ) : top_markets.slice(0, 6).map((m, i) => {
              const staked = parseFloat(m.total_staked || 0);
              const won = parseFloat(m.total_won || 0);
              const roi = staked > 0 ? (((won - staked) / staked) * 100).toFixed(1) : null;
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < Math.min(top_markets.length, 6) - 1 ? `1px solid ${T.border}` : 'none', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{m.title}</div>
                    <div style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.category} · {m.trade_count} trades</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.yes, fontFamily: FONT_MONO }}>KES {parseFloat(m.volume || 0).toLocaleString()}</div>
                    {roi !== null && (
                      <div style={{ fontSize: 10, color: parseFloat(roi) >= 0 ? T.yes : T.no, fontFamily: FONT_MONO }}>{parseFloat(roi) >= 0 ? '+' : ''}{roi}% ROI</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Wallet flow */}
          <div style={{ ...card }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Wallet Flow</div>
            {[
              { key: 'deposit',       label: 'Member Deposits',   color: T.yes   },
              { key: 'withdrawal',    label: 'Withdrawals',        color: T.no    },
              { key: 'trade_placed',  label: 'Trades Out',         color: T.accent },
              { key: 'trade_returned',label: 'Trade Returns',      color: T.gold  },
              { key: 'admin_credit',  label: 'Admin Credits',      color: T.yes   },
              { key: 'admin_debit',   label: 'Admin Debits',       color: T.no    },
            ].filter(f => flowMap[f.key]).map((f, i, arr) => (
              <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{f.label}</div>
                  <div style={{ fontSize: 10, color: T.textDim, fontFamily: FONT_MONO }}>{flowMap[f.key].count} transactions</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: f.color, fontFamily: FONT_MONO }}>
                  KES {flowMap[f.key].total.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Member breakdown */}
        <div style={{ ...card }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Member Breakdown</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, padding: '0 0 10px', borderBottom: `1px solid ${T.border}`, marginBottom: 8 }}>
            {['Member', 'Contributed', 'Share', 'Chama Trades'].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>
          {member_breakdown.map((m, i) => {
            const roleColor = { admin: T.accent, trader: T.yes, treasurer: T.gold, member: T.textMuted }[m.role] || T.textMuted;
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, padding: '10px 0', borderBottom: i < member_breakdown.length - 1 ? `1px solid ${T.border}` : 'none', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: T.accentDim, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: T.accent, flexShrink: 0 }}>
                    {m.username?.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{m.username}</div>
                    <div style={{ fontSize: 10, color: roleColor, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{m.role}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.yes, fontFamily: FONT_MONO }}>KES {parseFloat(m.contributed || 0).toLocaleString()}</div>
                <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT_MONO }}>{parseFloat(m.share_pct || 0).toFixed(1)}%</div>
                <div style={{ fontSize: 12, color: T.accent, fontFamily: FONT_MONO }}>{m.personal_chama_trades || 0}</div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

function TopBar({ T, onBack }) {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 32px',
      background: `${T.bg}ee`, backdropFilter: 'blur(16px)',
      borderBottom: `1px solid ${T.border}`,
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Analytics</div>
      <button onClick={onBack} style={{
        fontSize: 13, color: T.textMuted, textDecoration: 'none',
        padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
        border: `1px solid ${T.border}`, background: T.surface,
        fontFamily: FONT_SANS,
      }}>← Back</button>
    </nav>
  );
}